// Environment and Base URL types
export type Environment = 'dev' | 'uat' | 'app' | 'local';

// API Response Types
export interface BaseResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// User Types
export interface User {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  is_active: boolean;
  is_admin: boolean;
  unique_passport: string;
  created_at: string;
  needs_password_update?: boolean;
  on_free_trial?: boolean;
  free_trial_started_at?: string;
  free_trial_duration_days?: number;
  free_trial_ends_at?: number;
  last_login?: number;
  hubspot_subscription_id?: string;
  subscription_status?: string;
  subscription_plan?: string;
  subscription_current_period_end?: number;
  self_serve_subscription?: boolean;
  company_id?: number;
  company_role?: 'member' | 'manager';
  buying_role?: string | null;
  job_title?: string | null;
  pending_company_approval?: boolean;
  effective_membership_id?: number;
  login_streak_counter?: number;
}

// Company Types
export interface CompanyDomain {
  company_domain_id: number;
  company_id: number;
  domain: string;
  verification_status: 'pending' | 'verified' | 'failed';
  verification_method?: string;
  verified_at?: number;
  created_at: number;
  expires_at?: number;
}

export interface CompanyMember {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  company_role: 'member' | 'manager';
  pending_company_approval: boolean;
  membership_id?: number;
  is_active: boolean;
}

export interface Company {
  company_id: number;
  name: string;
  slug?: string;
  membership_id?: number;
  auto_approve_domain_signups: boolean;
  parent_group_id?: number;
  is_active: boolean;
  created_at: number;
  member_count?: number;
}

export interface CompanyDetail extends Company {
  members: CompanyMember[];
  domains: CompanyDomain[];
}

export interface DeletedCompany {
  company_id: number;
  name: string;
  slug: string | null;
  membership_id: number | null;
  created_at: number;
  deleted_at: number;
  deleted_by: number | null;
  member_count: number;
}

export interface ParentGroup {
  parent_group_id: number;
  name: string;
  slug?: string;
  is_active: boolean;
  created_at: number;
}

export interface ParentGroupMember {
  user_id: number;
  role: 'viewer' | 'admin';
  first_name: string;
  last_name: string;
  email: string;
}

export interface LoginRequest {
  user: {
    id?: number;
    username: string;
    password: string;
    agreedToTerms?: boolean;
    new_password?: string;
    errorHint?: string;
    unique_passport?: string;
  };
  recaptchaToken?: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Commentary Data Types
export type DriverReports =
  | 'ABS DRIVERS'
  | 'PA6 DRIVERS'
  | 'PA66 DRIVERS'
  | 'PC DRIVERS'
  | 'PE DRIVERS'
  | 'PET DRIVERS'
  | 'PP DRIVERS'
  | 'PS DRIVERS'
  | 'PVC DRIVERS';

export const DRIVER_REPORT_OPTIONS: DriverReports[] = [
  'ABS DRIVERS',
  'PA6 DRIVERS',
  'PA66 DRIVERS',
  'PC DRIVERS',
  'PE DRIVERS',
  'PET DRIVERS',
  'PP DRIVERS',
  'PS DRIVERS',
  'PVC DRIVERS',
];

export interface DriversCommentary {
  driverRptUnqId: string;
  date: string; // ISO date string
  driverReport: DriverReports | string;
  title?: string | null;
  marketOverviewBody?: string | null;
  marketOverviewBodyTrial?: string | null;
  supplyDemandTitle?: string | null;
  supplyDemandBody?: string | null;
  feedstocksBody?: string | null;
  internationalBody?: string | null;
  actionStrategies?: string | null;
  lastUpdatedBy?: string | null;
  lastUpdatedTime: string; // ISO datetime string
}

export interface PECommentary {
  date: string;
  pe_inv_cum_gains_draws: string;
  pe_prod_sales?: string | null;
  hdpe?: string | null;
  ldpe?: string | null;
  lldpe?: string | null;
  last_updated_by?: string | null;
  last_updated_time?: string | null;
}

export interface PPCommentary {
  date: string;
  pp_supply_demand: string;
  last_updated_by?: string | null;
  last_updated_time?: string | null;
}

export interface PSCommentary {
  date: string;
  ps_inv_cum_gains_draws: string;
  ps_supply_demand: string;
  ps_styrene_exports: string;
  ps_benzene_imports: string;
  ps_butadiene_imports: string;
  last_updated_by?: string | null;
  last_updated_time?: string | null;
}

export interface PVCCommentary {
  date: string;
  pvc_inv_cum_gains_draws: string;
  pvc_prod_sales: string;
  pvc_exports: string;
  pvc_domestic_sales: string;
  last_updated_by?: string | null;
  last_updated_time?: string | null;
}

export type CommentaryType = 'preliminary' | 'final';

export interface ForecastCommentary {
  forecastQtrDateId: string;
  forecastQtr: number; // 1-4
  commentary: string;
  polymer: string;
  lastUpdatedBy?: string | null;
  lastUpdatedTime?: string | null;
}

// Market Data Types
export interface CaproData {
  date: string;
  price: number;
  change?: number;
  [key: string]: any;
}

export interface EiaForecastData {
  date: string;
  year: number;
  month: number;
  quarter: number;
  brent_crude_oil_spot_price: number;
  wti_crude_oil_spot_price: number;
  natural_gas_henry_hub_price_usd: number;
}

export interface PetchemData {
  date: string;
  spot_ethylene?: number;
  ethane?: number;
  propane?: number;
  spot_rgp?: number;
  spot_pgp?: number;
  spot_benzene?: number;
  spot_styrene?: number;
  spot_butadiene?: number;
  wti_crude_oil?: number;
  brent_crude_oil?: number;
  nat_gas?: number;
  euro_to_us?: number;
  us_to_ca?: number;
  vam?: number;
  [key: string]: any;
}

export interface ChlorineData {
  date: string;
  price: number;
  volume?: number;
  [key: string]: any;
}

export interface DriverData {
  date: string;
  driverReport: string;
  marketOverview?: string;
  supplyDemand?: string;
  feedstocks?: string;
  international?: string;
  actionStrategies?: string;
  [key: string]: any;
}

export interface DriverPriceChangesData {
  id: number;
  date: string;
  driver: string;
  value: string | null;
}

export interface ChemOrbisData {
  date: string;
  region: string;
  product: string;
  price: number;
  [key: string]: any;
}

export interface PetMasterPricingData {
  date: string;
  product: string;
  price: number;
  [key: string]: any;
}

export interface ContractsData {
  date: string;
  product: string;
  contract_price: number;
  spot_price?: number;
  [key: string]: any;
}

export interface RefineryRatesData {
  date: string;
  capacity: number;
  utilization: number;
  production: number;
  [key: string]: any;
}

export interface NewsData {
  articles: NewsArticle[];
}

export interface NewsArticle {
  title: string;
  content: string;
  publication_date: string;
  source: string;
  url?: string;
}

// Chart Types
export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor: string | string[];
  borderColor: string;
  borderWidth?: number;
  fill?: boolean;
  tension?: number;
  pointRadius?: number;
}

export interface DriverCommentary {
  date: string;
  polymer: string;
  commentary: string;
  active: boolean;
}

// Access Levels
export const ACCESS_LEVELS = {
  BASE: 1,
  COMPOUND: 2,
  PRIME: 3,
  EMPLOYEE: 4,
} as const;

// Data Files Mapping
export const DATA_FILES = {
  CAPRO: 'caproData.json',
  PETCHEM: 'petchemData.json',
  CHLORINE: 'chlorineData.json',
  DRIVERS: 'driverData.json',
  DRIVER_CHARTS: 'driverCharts.json',
  DRIVER_PRICE_CHANGES: 'driverPriceChanges.json',
  CHEMORBIS: 'chemOrbisData.json',
  PETMASTERPRICING: 'petMasterPricingData.json',
  CONTRACTS: 'contractsData.json',
  REFINERY_RATES: 'refineryRatesData.json',
  NEWS_DATA: 'news_agg.json',
  DIESEL_PRICES: 'dieselPricesData.json',
  // ACC Monthly Snapshot data files
  ACC_PE: 'v_acc_pe.json',
  ACC_PP: 'v_acc_pp.json',
  ACC_PS: 'v_acc_ps.json',
  ACC_PVC: 'v_acc_pvc.json',
  ACC_PE_FINAL: 'v_acc_pe_final.json',
  ACC_PP_FINAL: 'v_acc_pp_final.json',
  ACC_PS_FINAL: 'v_acc_ps_final.json',
  ACC_PVC_FINAL: 'v_acc_pvc_final.json',
  FAIR_PRICE: 'fairPriceData.json',
  FORECAST: 'forecastData.json',
  FORECAST_COMMENTARY: 'forecastCommentary.json',
  EIA_FORECAST: 'vw_eia_steo_forecast.json',
  EMAIL_BULLETS: 'emailBulletsData.json',
  FREIGHTOS: 'freightosData.json',
  // PE Market Intel data files
  MI_PE_INVENTORY: 'pe/inventoryMetrics.json',
  MI_PE_PRODUCTION: 'pe/productionCapacity.json',
  MI_PE_MARKETS: 'pe/markets.json',
  MI_PE_RATES: 'pe/rates.json',
  MI_PE_USITC: 'pe/usitcTrade.json',
  PE_COMMENTARY: 'pe/commentary.json',
  HDPE_COMMENTARY: 'pe/hdpeCommentary.json',
  LDPE_COMMENTARY: 'pe/ldpeCommentary.json',
  LLDPE_COMMENTARY: 'pe/lldpeCommentary.json',
  MI_PP_RATES: 'pp/rates.json',
  MI_PP_INVENTORY: 'pp/inventoryMetrics.json',
  MI_PS_RATES: 'ps/rates.json',
  MI_PS_INVENTORY: 'ps/inventoryMetrics.json',
  MI_PVC_RATES: 'pvc/rates.json',
  MI_PVC_INVENTORY: 'pvc/inventoryMetrics.json',
  MI_PET_RATES: 'pet/rates.json',
  MI_PP_USITC: 'pp/usitcTrade.json',
  MI_PS_USITC: 'ps/usitcTrade.json',
  MI_PVC_USITC: 'pvc/usitcTrade.json',
  MI_PET_USITC: 'pet/usitcTrade.json',
} as const;


// API Path Prefixes

// Interactive demo 

/** One captured response that lost records to the trim. */
export interface DemoTrimmedEntry {
  key: string;
  records: number;
  kept: number;
}

/** One captured response that had numeric fields randomized. */
export interface DemoRandomizedEntry {
  key: string;
  rows: number;
  fields: string[];
}

export interface DemoSnapshotMeta {
  demo_snapshot_id: number;
  label: string | null;
  created_at: number;
  bundle_meta: {
    request_count?: number;
    captured_by?: string;
    version?: number;
    trimmed_months?: number;
    kept_years?: number;
    trimmed_records?: number;
    trimmed_entries?: DemoTrimmedEntry[];
    randomized_fields?: number;
    randomized_entries?: DemoRandomizedEntry[];
    commentary?: DemoCommentaryStatus;
  } | null;
  exists_on_disk: boolean;
}

/** Progress of the commentary rewrite that runs after a capture is uploaded. */
export interface DemoCommentaryStatus {
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  rows_total?: number;
  rows_done?: number;
  fields_written?: number;
  fields_failed?: number;
  rows_skipped?: number;
  error?: string;
}

export interface DemoSnapshotMetaResponse {
  active: DemoSnapshotMeta | null;
  /** Set while a capture is still having its commentary written. */
  pending?: DemoSnapshotMeta | null;
}

/** One calendar month's worth of demo sessions. */
export interface DemoMonthlyStats {
  month: string; // "2026-09"
  sessions: number;
  completed: number;
}

/** All-time demo funnel numbers, plus a month-by-month breakdown. */
export interface DemoAnalytics {
  sessions: number;
  completed: number;
  step_views: Record<string, number>;
  cta_clicks: Record<string, number>;
  monthly: DemoMonthlyStats[];
}

export type AnalyticsDays = 7 | 30 | 90 | 180 | 365;

export type AnalyticsCompanyStatus = 'paid' | 'trial' | 'lapsed';

export interface AnalyticsConfig {
  ga_configured: boolean;
  property_id: string | null;
  allowed_days: AnalyticsDays[];
}

export interface AnalyticsTrafficTotals {
  activeUsers: number;
  newUsers: number;
  sessions: number;
  screenPageViews: number;
  averageSessionDuration: number;
  engagementRate: number;
  bounceRate: number;
}

export interface AnalyticsDailyTraffic {
  date: string;
  app_users: number;
  app_new_users: number;
  app_sessions: number;
  marketing_users: number;
  marketing_new_users: number;
  marketing_sessions: number;
}

export interface AnalyticsFeatureUsage {
  feature: string;
  views: number;
  users: number;
  polymers: Record<string, number>;
}

export interface AnalyticsPolymerUsage {
  polymer: string;
  views: number;
  features: Record<string, number>;
}

export interface AnalyticsPageRow {
  path: string;
  views: number;
  users: number;
}

export interface AnalyticsGa {
  configured: boolean;
  errors: string[];
  fetched_at?: string;
  employees_excluded?: boolean;
  company_tracking?: boolean;
  realtime_active_users?: number;
  app?: AnalyticsTrafficTotals;
  marketing?: AnalyticsTrafficTotals;
  daily?: AnalyticsDailyTraffic[];
  feature_usage?: AnalyticsFeatureUsage[];
  polymer_usage?: AnalyticsPolymerUsage[];
  app_pages?: AnalyticsPageRow[];
  marketing_pages?: AnalyticsPageRow[];
  marketing_landing_pages?: { path: string; sessions: number; new_users: number }[];
  marketing_channels?: { channel: string; sessions: number; new_users: number }[];
  marketing_funnel?: { new_users: number; logged_into_app: number; not_logged_in: number; conversion_rate: number };
  signup?: {
    demo_users: number;
    demo_views: number;
    free_trial_page_users: number;
    sign_ups: number;
    logins: number;
    login_users: number;
    top_events: { event: string; count: number; users: number }[];
  };
  countries?: { country: string; users: number }[];
  devices?: { device: string; users: number }[];
  ga_companies?: { company_id: number; name: string; users: number; views: number }[];
}

export interface AnalyticsTopCompany {
  company_id: number;
  name: string;
  tier: string;
  status: AnalyticsCompanyStatus;
  active_users: number;
  total_users: number;
  last_seen: number | null;
  events: number;
}

export interface AnalyticsPlatform {
  total_companies: number;
  total_users: number;
  active_companies: Record<string, number>;
  active_users: Record<string, number>;
  companies_by_status: Record<AnalyticsCompanyStatus, number>;
  active_companies_by_status: Record<AnalyticsCompanyStatus, number>;
  active_companies_by_tier: { tier: string; companies: number }[];
  weekly_active: { week_start: string; companies: number; users: number }[];
  top_companies: AnalyticsTopCompany[];
  new_users: number;
  trials: {
    started_users: number;
    started_companies: number;
    converted_companies: number;
    conversion_rate: number;
    active_trial_users: number;
    expired_trial_users: number;
  };
}

export interface AnalyticsOverview {
  days: AnalyticsDays;
  start_date: string;
  end_date: string;
  generated_at: string;
  platform: AnalyticsPlatform;
  ga: AnalyticsGa;
}

export type HealthBand = 'healthy' | 'watch' | 'at_risk';

export type HealthComparison = 'aligned' | 'usage_stronger' | 'usage_weaker' | 'not_comparable';

export type HealthFlag = 'renewal_risk' | 'not_linked' | 'no_users' | 'not_in_hubspot' | 'access_mismatch';

export interface ClientHealthHubSpot {
  score: number | null;
  band: HealthBand | null;
  csm_sentiment: 'healthy' | 'neutral' | 'at_risk' | null;
  account_pulse: 'green' | 'yellow' | 'red' | null;
  account_pulse_summary: string | null;
  last_contacted: number | null;
  last_meeting: number | null;
  email_exchanges_90d: number | null;
  has_resinsmart_access: boolean | null;
}

export interface ClientHealthUsage {
  score: number;
  band: HealthBand;
  components: { recency: number; adoption: number; frequency: number; trend: number };
  reasons: string[];
  days_since_seen: number | null;
  active_users: number;
  total_users: number;
  active_days: number;
  events_30d: number;
  events_prev_30d: number;
  last_seen: number | null;
}

export type ClientMatchMethod =
  | 'hubspot_id'
  | 'company_domain'
  | 'user_domain'
  | 'contact_email'
  | 'contact_domain'
  | 'contact_user_domain';

export interface ClientHealthRow {
  name: string;
  hubspot_id: string | null;
  company_id: number | null;
  linked_companies: { company_id: number; name: string }[];
  match_method: ClientMatchMethod | null;
  tier: string | null;
  renewal_date: number | null;
  renewal_in_days: number | null;
  hubspot: ClientHealthHubSpot | null;
  usage: ClientHealthUsage | null;
  gap: number | null;
  comparison: HealthComparison;
  flags: HealthFlag[];
}

export interface ClientHealthResponse {
  configured: boolean;
  error: string | null;
  generated_at?: string;
  thresholds?: { healthy: number; watch: number; gap: number; renewal_days: number };
  summary: {
    total: number;
    aligned: number;
    usage_stronger: number;
    usage_weaker: number;
    not_comparable: number;
    renewal_risk: number;
  } | null;
  clients: ClientHealthRow[];
}

export interface DemoSnapshotUploadResponse {
  demo_snapshot_id: number;
  commentary?: DemoCommentaryStatus;
  request_count: number;
  created_at: number;
  trimmed_months: number;
  kept_years: number;
  trimmed_records: number;
  trimmed_entries: DemoTrimmedEntry[];
  randomized_fields: number;
  randomized_entries: DemoRandomizedEntry[];
}

export const API_PREFIX = {
  USER: '/user',
  GATEWAY: '/gateway',
  GATEWAY_USER: '/gateway/user',
  SYNAPSE: '/gateway/synapse',
  COMPANY: '/company',
  ADMIN: '/admin',
  DEVOPS: '/devops',
  ANALYTICS: '/analytics',
} as const;

export interface DevOpsTicket {
  id: number;
  url: string;
  title: string;
  description: string;
  requester_note: string;
  state: string;
  work_item_type: string;
  area_path: string;
  category: string | null;
  priority: number | null;
  customer_facing: boolean;
  in_validation: boolean;
  due_date: string | null;
  tags: string[];
  custom_tags: string[];
  assigned_to: string | null;
  assigned_to_display: string | null;
  epic_id: number | null;
  sprint: string;
  comment_count: number;
  created_at: string;
  changed_at: string;
}

export interface DevOpsEpic {
  id: number;
  title: string;
}

export interface DevOpsSprint {
  id: string;
  name: string;
  path: string;
  start_date: string | null;
  finish_date: string | null;
  time_frame: string | null;
}

export interface DevOpsSprintCreate {
  name: string;
  start_date: string;
  finish_date: string;
}

export interface DevOpsSprintUpdate {
  start_date?: string;
  finish_date?: string;
}

export interface DevOpsTicketCreate {
  title: string;
  description: string;
  category: string;
  due_date: string;
  priority?: string;
  customer_facing?: boolean;
  epic_id?: number | null;
  sprint?: string | null;
  assigned_to?: string | null;
  requested_by?: string | null;
}

export interface DevOpsTicketUpdate {
  title?: string;
  description?: string;
  assigned_to?: string | null;
  priority?: string;
  category?: string;
  customer_facing?: boolean;
  in_validation?: boolean;
  due_date?: string;
  state?: string;
  epic_id?: number | null;
  sprint?: string | null;
}

export interface DevOpsComment {
  id: number;
  text: string;
  created_at: string;
}

export interface DevOpsCommentCreate {
  text: string;
}

export interface DevOpsAttachment {
  url: string;
}

export interface DevOpsAssignee {
  email: string;
  name: string;
}

export interface DevOpsCategory {
  value: string;
  work_item_type: string;
  area_path: string;
}

export interface DevOpsSlaHours {
  default: number | null;
  customer_facing: number | null;
}

export interface DevOpsConfig {
  enabled: boolean;
  project: string;
  board_url: string;
  categories: DevOpsCategory[];
  priorities: [string, string][];
  sla_hours: Record<string, DevOpsSlaHours>;
  states: string[];
}

export interface DevOpsTicketList {
  tickets: DevOpsTicket[];
  total: number;
}

// Client Data Types
export interface ClientDataRawRow {
  client: string;
  resin_description: string | null;
  material_type: string | null;
  manufacturer: string | null;
  supplier: string | null;
  month_pounds_received: number | null;
  price: number | null;
  baseline_price_per_pound: number | null;
  month: string | null;
  monthly_variance: number | null;
  monthly_savings: number | null;
  monthly_spend: number | null;
  monthly_avoidance: number | null;
  project_id: string | null;
}

export interface ClientDataMonthly {
  month: string;
  actualSpend: number;
  plannedSpend: number;
  volume: number;
}

export interface ClientDataSku {
  sku: string;
  type: string;
  classification: string | null;
  supplier: string;
  location: string | null;
  plant: string;
  volume: number;
  spend: number;
  plannedSpend: number;
  vwpp: number;
  marketVwpp: number | null;
  previousVolume: number | null;
  previousSpend: number | null;
  previousVwpp: number | null;
  monthly: ClientDataMonthly[];
}

export interface ClientDataResponse {
  year: number;
  skus: ClientDataSku[];
}

// API Routes Mapping
export const API_ROUTES = {
  CLIENT_DATA: `${API_PREFIX.SYNAPSE}/m/client-data`,
  CAPRO: `${API_PREFIX.SYNAPSE}/w/capro`,
  PETCHEM: `${API_PREFIX.SYNAPSE}/d/rti-petchem`,
  CHLORINE: `${API_PREFIX.SYNAPSE}/m/chlorine`,
  CONTRACTS: `${API_PREFIX.SYNAPSE}/d/contracts`,
  PET_MASTER_PRICING: `${API_PREFIX.SYNAPSE}/w/pet-master-pricing`,
  REFINERY_RATES: `${API_PREFIX.SYNAPSE}/w/refinery-rates`,
  CHEM_ORBIS: `${API_PREFIX.SYNAPSE}/w/orbis-price-wizard`,
  DRIVER_COMMENTARY: `${API_PREFIX.SYNAPSE}/w/driver-commentary-data`,
  DRIVER_PRICE_CHANGES: `${API_PREFIX.SYNAPSE}/w/driver-price-changes`,
  DRIVER_PRICE_CHANGES_PENDING: `${API_PREFIX.SYNAPSE}/w/driver-price-changes/pending`,
  DRIVER_PRICE_CHANGES_COMMIT: `${API_PREFIX.SYNAPSE}/w/driver-price-changes/commit`,
  DRIVER_PRICE_CHANGES_CANCEL: `${API_PREFIX.SYNAPSE}/w/driver-price-changes/cancel`,
  NEWS: `${API_PREFIX.SYNAPSE}/w/news`,
  EMAIL_BULLETS: `${API_PREFIX.SYNAPSE}/o/commentary/bullets`,
  FREIGHTOS: `${API_PREFIX.SYNAPSE}/o/freightos`,
  AUTHORIZE: `${API_PREFIX.USER}/authorize`,
  FREE_TRIAL_SIGNUP: `${API_PREFIX.USER}/free-trial/signup`,
  FREE_TRIAL_DOMAINS: `${API_PREFIX.USER}/free-trial/domains`,
  FREE_TRIAL_CHECK_DOMAIN: `${API_PREFIX.USER}/free-trial/check-domain`,
  VERIFY_EMAIL: `${API_PREFIX.USER}/verify-email`,
  RESEND_VERIFICATION: `${API_PREFIX.USER}/resend-verification`,
  REPORT_USER: `${API_PREFIX.GATEWAY}/report/user/`,
  MEMBERSHIP_ALL: `${API_PREFIX.GATEWAY}/membership/all`,
  USER_ACCESS_REQUEST: `${API_PREFIX.GATEWAY}/user-access-request`,
  USER_ACCESS_REQUEST_BULK: `${API_PREFIX.GATEWAY}/user-access-request/bulk`,
  USER_ACCESS_REQUEST_RESOLVE: `${API_PREFIX.GATEWAY}/user-access-request/resolve/`,
  CLIENT_LEAD: `${API_PREFIX.GATEWAY}/client-lead/`,
  CLIENT_LEAD_ALL: `${API_PREFIX.GATEWAY}/client-lead/all`,
  REPORT_ALL: `${API_PREFIX.GATEWAY}/report/all`,
  REPORT: `${API_PREFIX.GATEWAY}/report/`,
  ADMIN_REPORTS: `${API_PREFIX.GATEWAY}/admin/reports`,
  SAVE_DATA: `${API_PREFIX.GATEWAY}/save-data`,
  UTILS_REPORT_OPTIONS: `${API_PREFIX.GATEWAY}/utils/report-options`,
  USER_SESSION: `${API_PREFIX.GATEWAY}/user-session`,
  USER_SESSION_CLOSE: `${API_PREFIX.GATEWAY}/user-session/close`,
  CACHE_SAVE: `${API_PREFIX.GATEWAY}/cache/save`,
  CACHE_READ: `${API_PREFIX.GATEWAY}/cache/read`,
  CACHE_VERSION: `${API_PREFIX.GATEWAY}/cache/version`,
  DRIVER_CHARTS_CONFIG: `${API_PREFIX.GATEWAY}/config/driver-charts`,
  PASSWORD_UPDATE: `${API_PREFIX.USER}/password/update`,
  ADMIN_RESET_PASSWORD: `${API_PREFIX.USER}/admin/reset-password`,
  UPDATE_PROFILE: `${API_PREFIX.USER}/profile/update`,
  TERMS_ACCEPT: `${API_PREFIX.USER}/terms-of-use/accept`,
  USER_ALL: `${API_PREFIX.USER}/all`,
  USER_IMPERSONATE: `${API_PREFIX.USER}/impersonate`,
  USER_HS_ACTIVITY: `${API_PREFIX.USER}/hs-activity/`,
  USER_ACTIVITY: `${API_PREFIX.USER}/activity/`,
  USER_FROM_CLIENT: `${API_PREFIX.USER}/from-client/`,
  USER_PREFERENCES: `${API_PREFIX.USER}/preferences/`,
  USER_ROOT: `${API_PREFIX.USER}/`,
  USER_PASSKEY_BEGIN: `${API_PREFIX.USER}/passkey/begin`,
  USER_PASSKEY_FINISH: `${API_PREFIX.USER}/passkey/finish`,
  USER_PASSKEY_REGISTER_BEGIN: `${API_PREFIX.USER}/passkey/register/begin`,
  USER_PASSKEY_REGISTER_FINISH: `${API_PREFIX.USER}/passkey/register/finish`,
  USER_PASSKEYS_LIST: `${API_PREFIX.USER}/passkeys`,
  USER_PASSKEYS_DELETE: `${API_PREFIX.USER}/passkeys`,
  FREE_EMAIL_DOMAINS: `${API_PREFIX.GATEWAY}/config/free-email-domains`,

  // ACC Monthly Snapshot routes
  ACC_PE: `${API_PREFIX.SYNAPSE}/m/v_acc_pe`,
  ACC_PP: `${API_PREFIX.SYNAPSE}/m/v_acc_pp`,
  ACC_PS: `${API_PREFIX.SYNAPSE}/m/v_acc_ps`,
  ACC_PVC: `${API_PREFIX.SYNAPSE}/m/v_acc_pvc`,
  ACC_PE_FINAL: `${API_PREFIX.SYNAPSE}/m/v_acc_pe_final`,
  ACC_PP_FINAL: `${API_PREFIX.SYNAPSE}/m/v_acc_pp_final`,
  ACC_PS_FINAL: `${API_PREFIX.SYNAPSE}/m/v_acc_ps_final`,
  ACC_PVC_FINAL: `${API_PREFIX.SYNAPSE}/m/v_acc_pvc_final`,

  // Pricing Forecast routes
  FAIR_PRICE: `${API_PREFIX.SYNAPSE}/m/forecasts/fair-price-index/date-range`,
  FORECAST: `${API_PREFIX.SYNAPSE}/m/forecasts/graph-data/all`,
  FORECAST_COMMENTARY: `${API_PREFIX.SYNAPSE}/m/forecasts/commentary-data/active`,

  // EIA Forecast routes
  EIA_FORECAST: `${API_PREFIX.SYNAPSE}/m/eia/forecast`,

  // Global Data Refresh Coordination routes
  GLOBAL_REFRESH_STATUS: `${API_PREFIX.GATEWAY}/global-refresh/status`,
  GLOBAL_REFRESH_REQUEST: `${API_PREFIX.GATEWAY}/global-refresh/request`,
  GLOBAL_REFRESH_CHECK: `${API_PREFIX.GATEWAY}/global-refresh/check-and-trigger`,
  GLOBAL_REFRESH_RESET: `${API_PREFIX.GATEWAY}/global-refresh/reset`,
  GLOBAL_REFRESH_SELECTIVE: `${API_PREFIX.GATEWAY}/global-refresh/selective`,

  // Password Reset routes
  PASSWORD_RESET_REQUEST: `${API_PREFIX.GATEWAY}/password-reset/request`,
  PASSWORD_RESET_VERIFY: `${API_PREFIX.GATEWAY}/password-reset/verify`,
  PASSWORD_RESET_COMPLETE: `${API_PREFIX.GATEWAY}/password-reset/complete`,

  // Microsoft/PowerBI utilities
  UTILS_MICROSOFT_POWERBI_ACQUIRE_ACCESS_TOKEN: `${API_PREFIX.GATEWAY}/utils/microsoft/powerbi/acquire-access-token`,

  // Daily Spot & Contracts Data Entry
  DAILY_SPOT: `${API_PREFIX.SYNAPSE}/d/daily-spot-data`,
  DAILY_SPOT_PENDING: `${API_PREFIX.SYNAPSE}/d/daily-spot-data/pending`,
  DAILY_SPOT_COMMIT: `${API_PREFIX.SYNAPSE}/d/daily-spot-data/commit`,
  DAILY_SPOT_CANCEL: `${API_PREFIX.SYNAPSE}/d/daily-spot-data/cancel`,
  DAILY_CONTRACTS: `${API_PREFIX.SYNAPSE}/d/daily-contracts-data`,
  DAILY_CONTRACTS_PENDING: `${API_PREFIX.SYNAPSE}/d/daily-contracts-data/pending`,
  DAILY_CONTRACTS_COMMIT: `${API_PREFIX.SYNAPSE}/d/daily-contracts-data/commit`,
  DAILY_CONTRACTS_CANCEL: `${API_PREFIX.SYNAPSE}/d/daily-contracts-data/cancel`,

  // Commentary Data Management routes
  COMMENTARY_DRIVERS: `${API_PREFIX.SYNAPSE}/w/driver-commentary-data`,
  COMMENTARY_DRIVERS_COUNT: `${API_PREFIX.SYNAPSE}/w/driver-commentary-data/count`,
  COMMENTARY_DRIVERS_LATEST: `${API_PREFIX.SYNAPSE}/w/driver-commentary-data/latest-update-time`,
  COMMENTARY_DRIVERS_STAGING: `${API_PREFIX.SYNAPSE}/w/drivers/commentary-data`,
  COMMENTARY_DRIVERS_PENDING: `${API_PREFIX.SYNAPSE}/w/drivers/commentary-data/pending`,
  COMMENTARY_DRIVERS_COMMIT: `${API_PREFIX.SYNAPSE}/w/drivers/commentary-data/commit`,
  COMMENTARY_DRIVERS_CANCEL: `${API_PREFIX.SYNAPSE}/w/drivers/commentary-data/cancel`,
  COMMENTARY_PE: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data`,
  COMMENTARY_PE_COUNT: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data/count`,
  COMMENTARY_PE_LATEST: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data/latest-update-time`,
  COMMENTARY_PE_PENDING: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data/pending`,
  COMMENTARY_PE_COMMIT: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data/commit`,
  COMMENTARY_PE_CANCEL: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data/cancel`,
  COMMENTARY_PE_CANCEL_ONE: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data/cancel`,
  COMMENTARY_PE_FINAL: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data-final`,
  COMMENTARY_PE_FINAL_PENDING: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data-final/pending`,
  COMMENTARY_PE_FINAL_COMMIT: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data-final/commit`,
  COMMENTARY_PE_FINAL_CANCEL: `${API_PREFIX.SYNAPSE}/m/peacc-commentary-data-final/cancel`,
  COMMENTARY_PP: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data`,
  COMMENTARY_PP_COUNT: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data/count`,
  COMMENTARY_PP_LATEST: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data/latest-update-time`,
  COMMENTARY_PP_PENDING: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data/pending`,
  COMMENTARY_PP_COMMIT: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data/commit`,
  COMMENTARY_PP_CANCEL: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data/cancel`,
  COMMENTARY_PP_CANCEL_ONE: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data/cancel`,
  COMMENTARY_PP_FINAL: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data-final`,
  COMMENTARY_PP_FINAL_PENDING: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data-final/pending`,
  COMMENTARY_PP_FINAL_COMMIT: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data-final/commit`,
  COMMENTARY_PP_FINAL_CANCEL: `${API_PREFIX.SYNAPSE}/m/ppacc-commentary-data-final/cancel`,
  COMMENTARY_PS: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data`,
  COMMENTARY_PS_COUNT: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data/count`,
  COMMENTARY_PS_LATEST: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data/latest-update-time`,
  COMMENTARY_PS_PENDING: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data/pending`,
  COMMENTARY_PS_COMMIT: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data/commit`,
  COMMENTARY_PS_CANCEL: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data/cancel`,
  COMMENTARY_PS_CANCEL_ONE: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data/cancel`,
  COMMENTARY_PS_FINAL: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data-final`,
  COMMENTARY_PS_FINAL_PENDING: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data-final/pending`,
  COMMENTARY_PS_FINAL_COMMIT: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data-final/commit`,
  COMMENTARY_PS_FINAL_CANCEL: `${API_PREFIX.SYNAPSE}/m/psacc-commentary-data-final/cancel`,
  COMMENTARY_PVC: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data`,
  COMMENTARY_PVC_COUNT: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data/count`,
  COMMENTARY_PVC_LATEST: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data/latest-update-time`,
  COMMENTARY_PVC_PENDING: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data/pending`,
  COMMENTARY_PVC_COMMIT: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data/commit`,
  COMMENTARY_PVC_CANCEL: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data/cancel`,
  COMMENTARY_PVC_CANCEL_ONE: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data/cancel`,
  COMMENTARY_PVC_FINAL: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data-final`,
  COMMENTARY_PVC_FINAL_PENDING: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data-final/pending`,
  COMMENTARY_PVC_FINAL_COMMIT: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data-final/commit`,
  COMMENTARY_PVC_FINAL_CANCEL: `${API_PREFIX.SYNAPSE}/m/pvcacc-commentary-data-final/cancel`,
  COMMENTARY_FORECAST: `${API_PREFIX.SYNAPSE}/m/forecasts/commentary-data`,
  COMMENTARY_FORECAST_COUNT: `${API_PREFIX.SYNAPSE}/m/forecasts/commentary-data/count`,
  COMMENTARY_FORECAST_PENDING: `${API_PREFIX.SYNAPSE}/m/forecasts/commentary-data/pending`,
  COMMENTARY_FORECAST_COMMIT: `${API_PREFIX.SYNAPSE}/m/forecasts/commentary-data/commit`,
  COMMENTARY_FORECAST_CANCEL: `${API_PREFIX.SYNAPSE}/m/forecasts/commentary-data/cancel`,
  COMMIT_CHANGES: `${API_PREFIX.SYNAPSE}/m/commit-changes`,

  BILLING_PLANS: `${API_PREFIX.GATEWAY}/billing/plans`,
  BILLING_CHECKOUT: `${API_PREFIX.GATEWAY}/billing/checkout`,
  BILLING_SUBSCRIPTION: `${API_PREFIX.GATEWAY}/billing/subscription`,
  BILLING_SUBSCRIPTION_SYNC: `${API_PREFIX.GATEWAY}/billing/subscription/sync`,
  BILLING_CHECKOUT_CONFIRM: `${API_PREFIX.GATEWAY}/billing/checkout/confirm`,
  ADMIN_SUBSCRIPTIONS: `${API_PREFIX.GATEWAY}/billing/admin/subscriptions`,
  ADMIN_SUBSCRIPTION_AUDIT: `${API_PREFIX.GATEWAY}/billing/admin/subscription-audit`,
  BILLING_SUBSCRIPTION_CANCEL: `${API_PREFIX.GATEWAY}/billing/subscription/cancel`,
  BILLING_SUBSCRIPTION_PAUSE: `${API_PREFIX.GATEWAY}/billing/subscription/pause`,
  BILLING_SUBSCRIPTION_RESUME: `${API_PREFIX.GATEWAY}/billing/subscription/resume`,
  BILLING_INVOICES: `${API_PREFIX.GATEWAY}/billing/invoices`,
  BILLING_UPDATE_CARD_LINK: `${API_PREFIX.GATEWAY}/billing/payment-method/update-link`,
  BILLING_CONTACT: `${API_PREFIX.GATEWAY}/billing/contact`,

  DRIVERS_PDF_CHECK_CACHE: `${API_PREFIX.GATEWAY}/api/drivers/cached-pdf-exists`,
  DRIVERS_PDF_DOWNLOAD: `${API_PREFIX.GATEWAY}/api/drivers/download-cached-pdf`,
  DRIVERS_PDF_SAVE: `${API_PREFIX.GATEWAY}/api/drivers/save-pdf`,

  PETCHEM_FILE_CHECK_CACHE: `${API_PREFIX.GATEWAY}/api/petchem/cached-file-exists`,
  PETCHEM_FILE_DOWNLOAD: `${API_PREFIX.GATEWAY}/api/petchem/download-cached-file`,
  PETCHEM_FILE_SAVE: `${API_PREFIX.GATEWAY}/api/petchem/save-file`,

  ACC_PDF_CHECK_CACHE: `${API_PREFIX.GATEWAY}/api/acc/cached-pdf-exists`,
  ACC_PDF_DOWNLOAD: `${API_PREFIX.GATEWAY}/api/acc/download-cached-pdf`,
  ACC_PDF_SAVE: `${API_PREFIX.GATEWAY}/api/acc/save-pdf`,

  // Company management
  COMPANY: `${API_PREFIX.COMPANY}/`,
  COMPANY_MEMBERS: `${API_PREFIX.COMPANY}/members`,
  COMPANY_DOMAINS: `${API_PREFIX.COMPANY}/domains`,

  // Admin
  ADMIN_COMPANIES: `${API_PREFIX.ADMIN}/companies`,
  ADMIN_PARENT_GROUPS: `${API_PREFIX.ADMIN}/parent-groups`,
  ADMIN_SYNC_ALL_HUBSPOT: `${API_PREFIX.ADMIN}/sync-all-hubspot`,
  ADMIN_SYNC_ALL_HUBSPOT_STATUS: `${API_PREFIX.ADMIN}/sync-all-hubspot/status`,
  ADMIN_HUBSPOT_ID_SCAN: `${API_PREFIX.ADMIN}/companies/hubspot-ids/scan`,
  ADMIN_HUBSPOT_ID_APPLY: `${API_PREFIX.ADMIN}/companies/hubspot-ids/apply`,

  DEVOPS_CONFIG: `${API_PREFIX.DEVOPS}/config`,
  DEVOPS_ASSIGNEES: `${API_PREFIX.DEVOPS}/assignees`,
  DEVOPS_REQUESTERS: `${API_PREFIX.DEVOPS}/requesters`,
  DEVOPS_EPICS: `${API_PREFIX.DEVOPS}/epics`,
  DEVOPS_SPRINTS: `${API_PREFIX.DEVOPS}/sprints`,
  DEVOPS_ATTACHMENTS: `${API_PREFIX.DEVOPS}/attachments`,
  DEVOPS_TICKETS: `${API_PREFIX.DEVOPS}/tickets`,
  ANALYTICS_CONFIG: `${API_PREFIX.ANALYTICS}/config`,
  ANALYTICS_OVERVIEW: `${API_PREFIX.ANALYTICS}/overview`,
  ANALYTICS_CLIENT_HEALTH: `${API_PREFIX.ANALYTICS}/client-health`,

  // User Notifications (served from gateway.py → /gateway/user/*)
  USER_NOTIFICATIONS: `${API_PREFIX.GATEWAY_USER}/notifications`,
  USER_NOTIFICATIONS_SYNC: `${API_PREFIX.GATEWAY_USER}/notifications/sync-hubspot`,
  USER_NOTIFICATIONS_AUDIT: `${API_PREFIX.GATEWAY_USER}/notifications/audit`,
  USER_DATA_NOTIFICATIONS: `${API_PREFIX.GATEWAY_USER}/data-notifications/preferences`,
  USER_DATA_NOTIFICATIONS_INBOX: `${API_PREFIX.GATEWAY_USER}/data-notifications/inbox`,
  USER_HOME_LAYOUT: `${API_PREFIX.GATEWAY_USER}/home-layout`,
  USER_ADMIN_MENU: `${API_PREFIX.GATEWAY_USER}/admin-menu`,

  // Admin — Available Subscriptions
  ADMIN_AVAILABLE_SUBSCRIPTIONS: `${API_PREFIX.GATEWAY}/admin/available-subscriptions`,
  ADMIN_TEST_NOTIFICATION: `${API_PREFIX.ADMIN}/notifications/test`,

  // Admin — Market Links & Promo Codes
  ADMIN_MARKET_LINKS: `${API_PREFIX.GATEWAY}/admin/market-links`,
  ADMIN_PROMO_CODES: `${API_PREFIX.GATEWAY}/admin/promo-codes`,
  BILLING_PROMO_VALIDATE: `${API_PREFIX.GATEWAY}/billing/promo/validate`,

  // Feeds / Email Manager
  FEEDS_STATE: `${API_PREFIX.GATEWAY}/feeds/state`,
  FEEDS_TRIGGER: `${API_PREFIX.GATEWAY}/feeds/trigger`,
  FEEDS_PREVIEW: `${API_PREFIX.GATEWAY}/feeds/preview`,
  FEEDS_TEST_SEND: `${API_PREFIX.GATEWAY}/feeds/test-send`,
  FEEDS_RSS_REFRESH: `${API_PREFIX.ADMIN}/rss/refresh`,
  FEEDS_REFRESH_RSS_SINGLE: `${API_PREFIX.GATEWAY}/feeds/refresh-rss`,

  // Feature Flags
  FEATURE_FLAGS: `${API_PREFIX.GATEWAY}/feature-flags`,
  ADMIN_FEATURE_FLAGS: `${API_PREFIX.GATEWAY}/admin/feature-flags`,

  // Interactive demo
  DEMO_SNAPSHOT: `${API_PREFIX.GATEWAY}/demo/snapshot`,
  DEMO_ADMIN_SNAPSHOT: `${API_PREFIX.GATEWAY}/demo/admin/snapshot`,
  DEMO_ADMIN_CAPTURE: `${API_PREFIX.GATEWAY}/demo/admin/capture`,
  DEMO_ADMIN_ANALYTICS: `${API_PREFIX.GATEWAY}/demo/admin/analytics`,

  // AI Summary
  AI_SNAPSHOT_SUMMARY: `${API_PREFIX.GATEWAY}/ai/summary`,
  AI_INSIGHTS: `${API_PREFIX.GATEWAY}/ai/insights`,
  AI_PROMPT_TEMPLATE: `${API_PREFIX.GATEWAY}/ai/prompt-template`,
  AI_INSIGHTS_REGENERATE: `${API_PREFIX.GATEWAY}/ai/insights`,
  AI_SUMMARY_REGENERATE: `${API_PREFIX.GATEWAY}/ai/summary`,

  // Nav Items
  NAV_ITEMS: `${API_PREFIX.GATEWAY}/nav-items`,
  ADMIN_NAV_ITEMS: `${API_PREFIX.GATEWAY}/admin/nav-items`,
} as const;

// Notification Types
export interface NotificationSystem {
  status: number;
  title: string | null;
  message: string | null;
  visible: boolean;
  timer: any;
}

// Modal Types
export interface RootModalSystem {
  visible: boolean;
  title: string | null;
  message: string | null;
  primaryAction: (() => void) | null;
  primaryActionLabel: string | null;
  primaryActionDanger: boolean;
}

// Report Types
export interface Report {
  report_id: number;
  name: string;
  abbreviation: string;
  sort_order: number;
  is_active: boolean;
  has_dashboard: boolean;
  has_acc: boolean;
  has_forecast: boolean;
  acc_membership_id: number;
  forecast_membership_id: number;
  membership_id: number;
  created_at: string;
  image_url?: string;
}

export interface AdminReportCreate {
  name: string;
  abbreviation: string;
  sort_order: number;
  has_dashboard: boolean;
  has_acc: boolean;
  has_forecast: boolean;
  membership_id: number;
  acc_membership_id: number;
  forecast_membership_id: number;
  is_active: boolean;
}

export interface AdminReportUpdate {
  name?: string;
  abbreviation?: string;
  sort_order?: number;
  has_dashboard?: boolean;
  has_acc?: boolean;
  has_forecast?: boolean;
  membership_id?: number;
  acc_membership_id?: number;
  forecast_membership_id?: number;
  is_active?: boolean;
}


// Client Lead Types
export interface ClientLead {
  client_lead_id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
}

// Membership Types
export interface Membership {
  membership_id: number;
  name: string;
  description: string;
  access_level: number;
}

// ACC Monthly Snapshot Types
export interface ACCSnapshotStats {
  value: number | string;
  change: string;
}

export interface ACCSnapshotData {
    [key: string]: any;
}

export interface PESnapshotData extends ACCSnapshotData {
  chart_hdpe_capacity?: number;
  chart_hdpe_production?: number;
  chart_hdpe_sales?: number | string;
  chart_ldpe_capacity?: number;
  chart_ldpe_production?: number;
  chart_ldpe_sales?: number | string;
  chart_lldpe_capacity?: number;
  chart_lldpe_production?: number;
  chart_lldpe_sales?: number | string;
  chart_pe_domestic_production?: number;
  chart_pe_inventory?: number;
  chart_pe_total_sales?: number;
  pe_inv_cum_gains_draws?: string;
  pe_prod_sales?: string;
  hdpe?: string;
  ldpe?: string;
  lldpe?: string;
  date: string;
  pe_days?: number;
  pe_demand?: number;
  pe_export?: number;
  pe_operating?: number;
  lldpe_days?: number;
  lldpe_demand?: number;
  lldpe_export?: number;
  lldpe_operating?: number;
  hdpe_days?: number;
  hdpe_demand?: number;
  hdpe_export?: number;
  hdpe_operating?: number;
  ldpe_days?: number;
  ldpe_demand?: number;
  ldpe_export?: number;
  ldpe_operating?: number;
  last_updated_by?: string;
  last_updated_time?: string;
}

export interface PPSnapshotData extends ACCSnapshotData {
  date: string;
  capacity: number;
  days_of_inventory: number;
  demand_rates: number;
  domestic_demand: number;
  domestic_production: number;
  export_rates: number;
  exports_total: number;
  inventory_change: number;
  inventory_change_monthly: number;
  inventory_daily: number;
  inventory_monthly: number;
  operating_rates: number;
  raw_capacity: number;
  total_sales_and_captive_use: number;
  pp_supply_demand?: string;
  last_updated_by?: string;
  last_updated_time?: string;
  record_date: string;
  imports?: number | null;
  blow_molding?: number | null;
  injection_molding?: number | null;
  extrusion_total?: number | null;
  film_total?: number | null;
  nonwovens?: number | null;
  fiber_filaments_total?: number | null;
  consumer_and_institutional_total?: number | null;
  compounders?: number | null;
}

export interface PSSnapshotData extends ACCSnapshotData {
  date: string;
  ps_days?: number;
  ps_demand_rates?: number;
  ps_operating?: number;
  ps_inventory_monthly?: number;
  chart_ps_inventory?: number;
  chart_ps_production?: number;
  chart_ps_sales?: number | string;
  chart_ps_capacity?: number;
  chart_bz_canada?: string | null;
  chart_bz_china?: string | null;
  chart_bz_europe?: string | null;
  chart_bz_latin_america?: string | null;
  chart_bz_mexico?: string | null;
  chart_bz_other?: string | null;
  chart_bz_pacific_rim?: string | null;
  chart_bd_canada?: string | null;
  chart_bd_china?: string | null;
  chart_bd_europe?: string | null;
  chart_bd_latin_america?: string | null;
  chart_bd_mexico?: string | null;
  chart_bd_other?: string | null;
  chart_bd_pacific_rim?: string | null;
  chart_sm_canada?: string | null;
  chart_sm_china?: string | null;
  chart_sm_europe?: string | null;
  chart_sm_latin_america?: string | null;
  chart_sm_mexico?: string | null;
  chart_sm_other?: string | null;
  chart_sm_pacific_rim?: string | null;
  ps_inv_cum_gains_draws?: string;
  ps_supply_demand?: string;
  ps_styrene_exports?: string;
  ps_benzene_imports?: string;
  ps_butadiene_imports?: string;
  last_updated_by?: string;
  last_updated_time?: string;
}

export interface PVCSnapshotData {
  date: string;
  capacity: number;
  days_of_inventory: number;
  demand_rates: string;
  domestic_demand: number;
  domestic_production: number;
  export_rates: string;
  exports_total: number;
  inventory_change_monthly: number;
  inventory_change: number;
  inventory_daily: number;
  inventory_monthly: number;
  operating_rates: string;
  record_date: string;
  total_sales_and_captive_use: number;
  pvc_inv_cum_gains_draws?: string;
  pvc_prod_sales?: string;
  pvc_exports?: string;
  pvc_domestic_sales?: string;
  LastUpdatedBy?: string;
  LastUpdatedTime?: string;
  export_africa?: number | null;
  export_canada?: number | null;
  export_china?: number | null;
  export_europe?: number | null;
  export_latin_america?: number | null;
  export_middle_east?: number | null;
  export_mexico?: number | null;
  export_Other?: number | null;
  export_pacific_rim?: number | null;
  segment_all_other_uses?: number | null;
  segment_fencing_and_decking?: number | null;
  segment_film_and_sheet?: number | null;
  segment_resellers_and_compounders?: number | null;
  segment_rigid_pipe_tubing?: number | null;
  segment_siding_skirting?: number | null;
  segment_extrusion_windows_and_doors?: number | null;
  segment_wire_and_cable?: number | null;
}

// Pricing Forecast Types
export interface FairPriceData {
  Date: string;
  HDPEBlowMLDG?: number;
  HDPEHMWFilm?: number;
  HDPEINJ?: number;
  LDPEGPFilm?: number;
  LLDPEButene?: number;
  LLDPEHexene?: number;
  PPHomo?: number;
  PPCopolymer?: number;
  PPRandomCopolymer?: number;
  PVC?: number;
  PETFormula?: number;
  GPPS?: number;
  HIPS?: number;
}

export interface ForecastData {
  Date_Quarterly: string;
  PE_High?: number;
  PE_Low?: number;
  PP_Homo_High?: number;
  PP_Homo_Low?: number;
  PS_High?: number;
  PS_Low?: number;
  PVC_High?: number;
  PVC_Low?: number;
  PET_High?: number;
  PET_Low?: number;
}

export interface ForecastCommentary {
  forecastQtr: number;
  forecastYear: number;
  polymer: string;
  commentary: string;
  active: boolean;
  expanded?: boolean;
}

export interface ChartDataPoint {
  x: string;
  y: number | null;
}

export interface ChartData {
  fairPrice: ChartDataPoint[];
  mid: ChartDataPoint[];
  upper: ChartDataPoint[];
  lower: ChartDataPoint[];
  history: ChartDataPoint[];
}

export type PolymerType = 'HDPEBlowMLDG' | 'HDPEHMWFilm' | 'HDPEINJ' | 'LDPEGPFilm' | 'LLDPEButene' | 'LLDPEHexene' | 'PPHomo' | 'PPCopolymerImpact' | 'PPCopolymerRandom' | 'PVC' | 'PETFormula' | 'GPPS' | 'HIPS';

export const POLYMER_LABELS: Record<PolymerType, string> = {
  HDPEBlowMLDG: 'HDPE Blow Molding',
  HDPEHMWFilm: 'HDPE HMW Film',
  HDPEINJ: 'HDPE INJ',
  LDPEGPFilm: 'LDPE GP Film',
  LLDPEButene: 'LLDPE Butene',
  LLDPEHexene: 'LLDPE Hexene',
  PPHomo: 'PP Homopolymer',
  PPCopolymerImpact: 'PP Copolymer Impact',
  PPCopolymerRandom: 'PP Copolymer Random',
  PVC: 'PVC',
  PETFormula: 'PET',
  GPPS: 'GPPS',
  HIPS: 'HIPS'
};

// Driver Charts Configuration Types
export interface DriverChartFeed {
  name: string;
  measure: string;
  source: string;
  key: string;
  price: string;
  mt2lb_convert: string;
}

export interface DriverSpotSection {
  title: string;
  feeds: DriverChartFeed[];
}

export interface DriverChartValue {
  symbol: string;
  measure?: string;
  source: string;
  key: string;
  price: string;
  mt2lb_convert: string;
}

export interface DriverChart {
  title: string;
  dualaxis: boolean;
  values: DriverChartValue[];
  type: string;
}

export interface DriverChartData {
  driver: string;
  spot: DriverSpotSection[];
  chart: DriverChart[];
}

export type DriverChartsData = DriverChartData[];

// User Activity Types
export interface UserActivityItem {
  event: string;
  url: string;
  title?: string | null;
  timestamp: number;
  event_metadata?: Record<string, any>;
}

export interface UserActivityResponse {
  email: string;
  activities: UserActivityItem[];
  error?: string;
}

// Available Subscriptions (admin-managed source of truth)
export interface AvailableSubscription {
  id: string;
  hubspot_id?: string | null;
  subscription_key: string;
  name: string;
  description?: string | null;
  active: boolean;
  sort_order?: number | null;
}

export interface AvailableSubscriptionCreate {
  hubspot_id?: string | null;
  subscription_key: string;
  name: string;
  description?: string | null;
  active?: boolean;
  sort_order?: number | null;
}

export interface AvailableSubscriptionUpdate {
  hubspot_id?: string | null;
  name?: string;
  description?: string | null;
  active?: boolean;
  sort_order?: number | null;
}

// Market Links & Promo Codes (admin-managed checkout offer registry, ticket 673)
export type MarketLinkBillingKind = 'monthly' | 'annual';

export interface MarketLink {
  id: string;
  link_key: string;
  name: string;
  subtitle?: string | null;
  hubspot_payment_link_id: string;
  membership_id: number;
  billing_kind: MarketLinkBillingKind;
  display_price: number;
  display_cadence: string;
  display_note?: string | null;
  terms_note?: string | null;
  active: boolean;
  sort_order?: number | null;
}

export interface MarketLinkCreate {
  link_key: string;
  name: string;
  subtitle?: string | null;
  hubspot_payment_link_id: string;
  membership_id: number;
  billing_kind: MarketLinkBillingKind;
  display_price: number;
  display_cadence: string;
  display_note?: string | null;
  terms_note?: string | null;
  active?: boolean;
  sort_order?: number | null;
}

export interface MarketLinkUpdate {
  name?: string;
  subtitle?: string | null;
  hubspot_payment_link_id?: string;
  membership_id?: number;
  billing_kind?: MarketLinkBillingKind;
  display_price?: number;
  display_cadence?: string;
  display_note?: string | null;
  terms_note?: string | null;
  active?: boolean;
  sort_order?: number | null;
}

// URL-free projection served to non-admin clients — never carries hubspot_payment_link_id
export interface PublicMarketLink {
  market_link_id: string;
  name: string;
  subtitle?: string | null;
  billing_kind: MarketLinkBillingKind;
  display_price: number;
  display_cadence: string;
  display_note?: string | null;
  terms_note?: string | null;
}

export interface PromoCode {
  id: string;
  code: string;
  name: string;
  membership_id: number;
  effect_type: string;
  effect_config: Record<string, unknown>;
  status: 'active' | 'inactive';
  valid_from?: string | null;
  valid_until?: string | null;
  max_redemptions?: number | null;
  redemption_count: number;
  campaign_tag?: string | null;
  created_by_user_id?: number | null;
  market_link_ids: string[];
}

export interface PromoCodeCreate {
  code: string;
  name: string;
  membership_id: number;
  effect_type?: string;
  effect_config?: Record<string, unknown>;
  status?: 'active' | 'inactive';
  valid_from?: string | null;
  valid_until?: string | null;
  max_redemptions?: number | null;
  campaign_tag?: string | null;
  market_link_ids?: string[];
}

export interface PromoCodeUpdate {
  name?: string;
  membership_id?: number;
  effect_config?: Record<string, unknown>;
  status?: 'active' | 'inactive';
  valid_from?: string | null;
  valid_until?: string | null;
  max_redemptions?: number | null;
  campaign_tag?: string | null;
  market_link_ids?: string[];
}

export interface PromoCodeRedemption {
  id: string;
  promo_code_id: string;
  market_link_id?: string | null;
  user_id: number;
  company_id?: number | null;
  status: 'pending' | 'confirmed' | 'abandoned';
  hubspot_contact_id?: string | null;
  hubspot_subscription_id?: string | null;
  created_at: string;
  confirmed_at?: string | null;
}

export type PromoValidationReason =
  | 'unknown'
  | 'inactive'
  | 'not_yet_active'
  | 'expired'
  | 'wrong_tier'
  | 'maxed_out'
  | 'no_links_available'
  | 'link_not_available';

export interface PromoValidation {
  valid: boolean;
  reason?: PromoValidationReason | null;
  code_label?: string | null;
  valid_until?: string | null;
  links: PublicMarketLink[];
}

// Admin Subscriptions (Employee + Admin only, ticket 673)
export interface AdminSubscriptionRow {
  company_id: number;
  company_name: string;
  membership_id: number;
  plan_name?: string | null;
  billing_frequency?: string | null;
  status?: string | null;
  started_at?: number | null;
  period_end?: number | null;
  hubspot_subscription_id?: string | null;
  hubspot_record_url?: string | null;
}

export interface SubscriptionAuditChange {
  before: unknown;
  after: unknown;
}

export interface CompanySubscriptionAuditRow {
  id: string;
  company_id: number;
  company_name?: string | null;
  hubspot_subscription_id?: string | null;
  source: string;
  actor_user_id?: number | null;
  actor_email?: string | null;
  changes: Record<string, SubscriptionAuditChange>;
  created_at: string;
}

// Feature Flags
export const FEATURE_FLAG_VISIBILITY = ['none', 'admin', 'employee', 'prime', 'compound', 'all'] as const;
export type FeatureFlagVisibility = typeof FEATURE_FLAG_VISIBILITY[number];

export interface FeatureFlag {
  id: string;
  feature_key: string;
  name: string;
  description?: string | null;
  is_enabled: boolean;
  visibility: FeatureFlagVisibility;
  created_at: number;
  updated_at: number;
}

export interface FeatureFlagPublic {
  feature_key: string;
}

export interface FeatureFlagCreate {
  feature_key: string;
  name: string;
  description?: string | null;
  is_enabled?: boolean;
  visibility?: FeatureFlagVisibility;
}

export interface FeatureFlagUpdate {
  name?: string;
  description?: string | null;
  is_enabled?: boolean;
  visibility?: FeatureFlagVisibility;
}

export interface SnapshotAISummary {
  brief: string | null;
  summary: string | null;
  generated_at: string;
  data_date: string;
  polymer: string;
  commentary_type: string;
  cached: boolean;
  configured?: boolean;
}

export interface AIInsight {
  brief: string | null;
  summary: string | null;
  generated_at: string;
  data_date: string;
  context: string;
  cached: boolean;
  configured?: boolean;
}

export interface AiPromptTemplate {
  context: string;
  system_message: string;
  instructions: string;
  updated_at?: string;
  updated_by?: string | null;
}

export interface PromptOverrides {
  system_message?: string;
  instructions?: string;
  extra_context?: string;
  save_as_default?: boolean;
}

export interface NavItem {
  id: string;
  nav_key: string;
  label: string;
  path: string;
  icon_name: string;
  section: string;
  sort_order: number;
  is_enabled: boolean;
  min_membership_id: number | null;
  feature_flag_key: string | null;
  has_submenu: boolean;
  submenu_type: string | null;
  submenu_items: string | null;
  created_at: number;
  updated_at: number;
}

export interface NavItemPublic {
  nav_key: string;
  label: string;
  path: string;
  icon_name: string;
  section: string;
  sort_order: number;
  feature_flag_key?: string | null;
  has_submenu: boolean;
  submenu_type?: string | null;
  submenu_items?: string | null;
  min_membership_id?: number | null;
}

export interface NavItemCreate {
  nav_key: string;
  label: string;
  path: string;
  icon_name: string;
  section: string;
  sort_order: number;
  is_enabled: boolean;
  min_membership_id: number | null;
  feature_flag_key: string | null;
  has_submenu?: boolean;
  submenu_type?: string | null;
  submenu_items?: string | null;
}

export interface NavItemUpdate {
  label?: string;
  path?: string;
  icon_name?: string;
  section?: string;
  is_enabled?: boolean;
  min_membership_id?: number | null;
  sort_order?: number;
  feature_flag_key?: string | null;
  has_submenu?: boolean;
  submenu_type?: string | null;
  submenu_items?: string | null;
}

// Notification Types
export interface NotificationCatalogEntry {
  id: string;
  notification_type: 'email' | 'platform' | 'sms';
  channel_key: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  sort_order?: number;
}

export interface EmailSubscriptionState {
  id: string;
  user_id: number;
  subscription_key: string;
  desired_state: boolean;
  provider_state?: boolean;
  provider_contact_id?: string;
  last_synced_at?: string;
  last_sync_status: 'success' | 'failed' | 'pending';
  last_error?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationAuditEntry {
  id: number;
  user_id: number;
  action: 'subscribe' | 'unsubscribe' | 'resubscribe' | 'sync_success' | 'sync_failed';
  channel_key: string;
  new_state: boolean;
  previous_state?: boolean;
  trigger_source: 'user_ui' | 'hubspot_webhook' | 'reconciliation_job';
  metadata_json?: Record<string, any>;
  created_at: string;
}

export interface UserNotificationsResponse {
  catalog: NotificationCatalogEntry[];
  subscriptions: EmailSubscriptionState[];
  audit_trail?: NotificationAuditEntry[];
}

export interface UpdateSubscriptionRequest {
  enabled: boolean;
}

export type HubSpotLinkConfidence = 'high' | 'medium' | 'low';

export type HubSpotLinkIssue =
  | 'employee_company'
  | 'different_id_on_file'
  | 'hubspot_id_in_use'
  | 'multiple_hubspot_matches'
  | 'hubspot_matches_multiple_companies'
  | 'domain_from_users_only'
  | 'name_mismatch';

export interface HubSpotLinkCompany {
  company_id: number;
  name: string;
  claimed_domains: string[];
  user_domains: string[];
  is_active: boolean;
  hubspot_company_id: string | null;
}

export interface HubSpotLinkRecord {
  id: string;
  found: boolean;
  name: string | null;
  domains: string[];
  client_status: string | null;
  customer_tier: string | null;
  url: string | null;
}

export interface HubSpotLinkCandidate {
  company: HubSpotLinkCompany;
  hubspot: HubSpotLinkRecord;
  matched_domains: string[];
  domain_source: 'claimed' | 'users';
  name_similarity: number;
  confidence: HubSpotLinkConfidence;
  issues: HubSpotLinkIssue[];
  blocking: boolean;
  recommended: boolean;
}

export interface HubSpotExistingLink {
  company: HubSpotLinkCompany;
  hubspot: HubSpotLinkRecord;
  shared_domains: string[];
  name_similarity: number;
  problems: ('hubspot_record_not_found' | 'no_shared_domain' | 'name_mismatch')[];
}

export interface HubSpotIdScanStatus {
  job_id: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  started_at: number | null;
  finished_at: number | null;
  message: string;
  result: {
    candidates: HubSpotLinkCandidate[];
    existing_links: HubSpotExistingLink[];
    summary: {
      hubspot_companies_scanned: number;
      candidates: number;
      recommended: number;
      blocked: number;
      existing_links: number;
      existing_with_problems: number;
      companies_without_id: number;
    };
  } | null;
}

export interface HubSpotIdApplyResult {
  linked: number;
  rejected: number;
  results: {
    company_id: number;
    hubspot_company_id: string;
    status: 'linked' | 'rejected';
    reason?: string;
    shared_domains?: string[];
  }[];
}

export interface AdminHubSpotSyncStatus {
  job_id?: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  total_users: number;
  processed: number;
  synced: number;
  failed: number;
  started_at?: number | null;
  finished_at?: number | null;
  message?: string;
  errors?: Array<{ user_id: number; email: string; error: string }>;
}

declare global {
  interface Window {
    currentUser: User | null;
  }
}

// USITC Trade Data
export interface USITCDataPoint {
  date: string;
  product: number;
  importExport: string;
  countryId: number;
  volume: number;
  country?: string;
  region?: string;
}

// Widget Layout for Landing Page customization
export interface WidgetConfig {
  id: string;
  componentKey: string;
  position: number;
  size?: 'sm' | 'md' | 'lg';
}

export type WidgetLayout = WidgetConfig[];

export interface AdminMenuItemPref {
  key: string;
  starred: boolean;
}

export interface AdminMenuPreferences {
  items: AdminMenuItemPref[];
  etag?: string | null;
}

export interface UserSettingsResponse {
  user_setting_id: string;
  user_id: number;
  namespace: string;
  schema_version: number;
  payload: { items?: AdminMenuItemPref[] } & Record<string, unknown>;
  etag: string | null;
  updated_at: number;
  created_at: number;
  is_active: boolean;
}

export type PolymerDashboardTab =
  | 'fair-price'
  | 'totals'
  | 'inventory-change'
  | 'demand-production-capacity'
  | 'feedstocks'
  | 'feedstocks-dashboard'
  | 'usitc'
  | 'rates'
  | 'markets'
  | 'exports'
  | 'running-daily'
  | 'averages'
  | 'refinery-rates';

export interface DataNotificationPreference {
  id?: number;
  user_id: number;
  channel_key: string;
  email_enabled: boolean;
  inapp_enabled: boolean;
  push_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DeviceToken {
  id?: number;
  user_id: number;
  push_token: string;
  platform: 'ios' | 'android';
  created_at?: string;
  last_seen_at?: string;
}

export interface InAppNotification {
  id: number;
  user_id: number;
  channel_key: string;
  title: string;
  message: string;
  payload_json?: Record<string, unknown> | null;
  is_read: boolean;
  created_at?: string;
}

export interface FreightosData {
  date: string;
  section: string;
  tradelane: string;
  origin: string;
  destination: string;
  mode: string;
  maximum: number | null;
  high: number | null;
  medium_high: number | null;
  medium: number | null;
  medium_low: number | null;
  low: number | null;
  minimum: number | null;
  average: number | null;
}

export interface DieselPriceRow {
  date: string;
  US: number | null;
  East_Coast: number | null;
  New_England: number | null;
  Central_Atlantic: number | null;
  Lower_Atlantic: number | null;
  Midwest: number | null;
  Gulf_Coast: number | null;
  Rocky_Mountain: number | null;
  West_Coast: number | null;
  West_Coast_Excl_California: number | null;
  California: number | null;
}